
import React, { useState } from 'react';
import { Upload, X, FileText, Check } from 'lucide-react';
import { Button } from "@/components/ui/button";

interface FileUploadFieldProps {
  value?: File | null;
  onChange: (file: File | null) => void;
  maxSize?: number; // in MB
  accept?: string;
  placeholder?: string;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({
  value,
  onChange,
  maxSize = 10, // Default max size: 10MB
  accept = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png',
  placeholder
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file: File) => {
    setError(null);
    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      setError(`File is too large. Maximum size is ${maxSize}MB.`);
      return;
    }

    // Validate file type from accept string
    const acceptTypes = accept.split(',');
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const validType = acceptTypes.some(type => {
      // Handle mime types or extensions
      if (type.startsWith('.')) {
        return type.toLowerCase() === fileExtension;
      }
      return file.type.match(type);
    });

    if (!validType) {
      setError('Invalid file type. Please upload a supported file format.');
      return;
    }

    onChange(file);
  };

  const removeFile = () => {
    onChange(null);
  };

  return (
    <div className="w-full">
      {!value ? (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-md p-6 transition-all duration-300 text-center ${
            dragActive 
              ? 'border-purple-500 bg-purple-500/10' 
              : 'border-gray-700 hover:border-purple-500 bg-gray-800/50 hover:bg-gray-800/70'
          }`}
        >
          <input
            id="file-upload"
            type="file"
            accept={accept}
            onChange={handleChange}
            className="hidden"
          />
          <label
            htmlFor="file-upload"
            className="flex flex-col items-center justify-center cursor-pointer"
          >
            <Upload className="w-8 h-8 mb-2 text-gray-400" />
            <span className="text-sm text-gray-300">
              {placeholder || 'Drag and drop your file here or click to browse'}
            </span>
            <span className="mt-2 text-xs text-gray-500">
              Max size: {maxSize}MB | Formats: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG
            </span>
          </label>
        </div>
      ) : (
        <div className="flex items-center justify-between p-4 bg-gray-800 border border-gray-700 rounded-md animate-scale-in">
          <div className="flex items-center">
            <div className="flex items-center justify-center w-10 h-10 bg-purple-500/20 rounded-full text-purple-400">
              <FileText size={20} />
            </div>
            <div className="ml-3 overflow-hidden">
              <p className="text-sm font-medium text-gray-200 truncate">{value.name}</p>
              <p className="text-xs text-gray-400">
                {(value.size / 1024 / 1024).toFixed(2)}MB
              </p>
            </div>
          </div>
          <Button 
            type="button" 
            variant="ghost" 
            size="sm" 
            onClick={removeFile}
            className="hover:bg-red-500/20 hover:text-red-400"
          >
            <X size={16} />
          </Button>
        </div>
      )}
      
      {error && (
        <p className="mt-2 text-sm text-red-400 animate-fade-in">{error}</p>
      )}
    </div>
  );
};

export default FileUploadField;
